import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { AccessTargetInput } from "../api/accessManagement";
import { ApiError, NetworkError } from "../api/client";
import {
  readMfaRecoveryTarget,
  requestMfaRecovery,
  searchMfaRecoveryTargets,
  type MfaRecoveryReasonCode,
  type MfaRecoveryRequestResult,
  type MfaRecoveryTarget,
} from "../api/mfaRecovery";
import { friendlyErrorMessage } from "../api/errors";
import { useAuth } from "../auth/AuthContext";
import { AccessTargetPicker } from "../components/access/AccessTargetPicker";
import { Button } from "../components/Button";
import { StatusBanner } from "../components/StatusBanner";

type ViewState = "idle" | "review" | "submitting" | "success" | "expired" | "conflict" | "error";
type RecoveryFormState = {
  target: MfaRecoveryTarget | null;
  currentMemberId: string | null;
  reasonCode: MfaRecoveryReasonCode | "";
  reasonDetail: string;
  confirmedInPerson: boolean;
};

const RECOVERY_TARGET_QUERY_MAX_LENGTH = 80;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const REASONS: Array<{ value: MfaRecoveryReasonCode; label: string }> = [
  { value: "device_lost", label: "Dispositivo perdido" },
  { value: "device_stolen", label: "Dispositivo furtado" },
  { value: "device_damaged", label: "Dispositivo danificado" },
  { value: "authenticator_unavailable", label: "Aplicativo autenticador indisponível" },
];

function messageForError(cause: unknown): string {
  if (cause instanceof ApiError && cause.status === 409 && cause.code === "recovery_expired") {
    return "A solicitação expirou. Registre uma nova solicitação presencial para continuar.";
  }
  if (cause instanceof ApiError && cause.status === 409) return "Já existe uma solicitação de recuperação ativa para este titular.";
  if (cause instanceof ApiError && cause.status === 403) return "Esta operação exige o acesso de Secretaria/DAF autorizado.";
  if (cause instanceof ApiError || cause instanceof NetworkError) return friendlyErrorMessage(cause);
  return "Não foi possível registrar a solicitação agora. Tente novamente.";
}

function viewForError(cause: unknown): ViewState {
  if (cause instanceof ApiError && cause.status === 409 && cause.code === "recovery_expired") return "expired";
  return cause instanceof ApiError && cause.status === 409 ? "conflict" : "error";
}

function validateForm({ target, currentMemberId, reasonCode, reasonDetail, confirmedInPerson }: RecoveryFormState): string | null {
  if (!target?.member_id) return "Selecione o associado titular da recuperação.";
  if (target.member_id === currentMemberId) return "A solicitação não pode ser aberta para a sua própria conta.";
  if (!reasonCode) return "Selecione o motivo da recuperação.";
  if (reasonDetail.length > 280 || /[\r\n]/.test(reasonDetail)) return "O complemento deve ter até 280 caracteres e não pode conter quebras de linha.";
  if (!confirmedInPerson) return "Confirme que o titular está presente na instituição antes de registrar a solicitação.";
  return null;
}

function reasonLabel(reasonCode: MfaRecoveryReasonCode): string {
  return REASONS.find((reason) => reason.value === reasonCode)?.label ?? reasonCode;
}

function initialMemberTarget(memberId: string | null, currentMemberId: string | null): { member_id: string } | undefined {
  if (!memberId || !UUID.test(memberId) || memberId === currentMemberId) return undefined;
  return { member_id: memberId };
}

function readRecoveryTarget(accessToken: string, input: AccessTargetInput): Promise<MfaRecoveryTarget> {
  if (!input.member_id) return Promise.reject(new Error("O alvo de recuperação deve ser um associado."));
  return readMfaRecoveryTarget(accessToken, input.member_id);
}

function searchRecoveryTargets(accessToken: string, query: string): Promise<MfaRecoveryTarget[]> {
  return searchMfaRecoveryTargets(accessToken, query);
}

function RecoverySuccessPanel({ result, onReset }: { result: MfaRecoveryRequestResult; onReset: () => void }) {
  return (
    <section className="mfa-recovery-request__success" aria-labelledby="mfa-recovery-success-title">
      <StatusBanner tone="success">
        {result.mode === "assisted_in_person"
          ? "Recuperação autorizada no atendimento presencial. O titular poderá concluir no próprio login."
          : "Solicitação registrada e encaminhada para aprovação independente."}
      </StatusBanner>
      <h2 id="mfa-recovery-success-title">Protocolo de recuperação</h2>
      <dl className="mfa-recovery-request__summary">
        <div><dt>Protocolo</dt><dd><code>{result.protocol_number}</code></dd></div>
        <div><dt>Status</dt><dd>{result.mode === "assisted_in_person" ? "Autorizada para conclusão pelo titular" : "Aguardando aprovação independente"}</dd></div>
        <div><dt>Prazo</dt><dd>{result.mode === "assisted_in_person" ? "15 minutos" : "Até 24 horas"}</dd></div>
      </dl>
      <p className="form-card__note">Guarde o protocolo para acompanhamento institucional. Nenhum fator é removido nesta etapa.</p>
      <Button type="button" variant="secondary" onClick={onReset}>Registrar outra solicitação</Button>
    </section>
  );
}

function RecoveryExpiredPanel({ onReset }: { onReset: () => void }) {
  return (
    <section className="mfa-recovery-request__success" aria-labelledby="mfa-recovery-expired-title">
      <StatusBanner tone="warning">A solicitação de recuperação expirou antes de ser concluída.</StatusBanner>
      <h2 id="mfa-recovery-expired-title">Solicitação expirada</h2>
      <p className="form-card__note">Registre uma nova solicitação presencial. O sistema emitirá um novo protocolo.</p>
      <Button type="button" variant="secondary" onClick={onReset}>Registrar nova solicitação</Button>
    </section>
  );
}

function RecoveryReviewPanel({
  target,
  reasonCode,
  onEdit,
  onConfirm,
}: {
  target: MfaRecoveryTarget;
  reasonCode: MfaRecoveryReasonCode;
  onEdit: () => void;
  onConfirm: () => void;
}) {
  return (
    <section className="mfa-recovery-request__review-panel" aria-labelledby="mfa-recovery-review-title">
      <StatusBanner tone="info">Confira os dados antes de registrar a solicitação.</StatusBanner>
      <h2 id="mfa-recovery-review-title">Revise a solicitação</h2>
      <dl className="mfa-recovery-request__summary">
        <div><dt>Titular</dt><dd>{target.display_name}</dd></div>
        <div><dt>Motivo</dt><dd>{reasonLabel(reasonCode)}</dd></div>
        <div><dt>Atendimento</dt><dd>Presencial confirmado</dd></div>
      </dl>
      <div className="form-actions mfa-recovery-request__actions">
        <Button type="button" variant="secondary" onClick={onEdit}>Editar dados</Button>
        <Button type="button" onClick={onConfirm}>Confirmar e registrar</Button>
      </div>
    </section>
  );
}

function RecoveryNotice() {
  return (
    <div className="mfa-recovery-request__notice">
      <strong>Atendimento presencial</strong>
      <p>Confirme a presença do titular. O sistema gera o protocolo rastreável; não registre senha, código ou segredo neste formulário.</p>
    </div>
  );
}

function renderRecoveryTerminalState({
  view,
  result,
  target,
  reasonCode,
  onReset,
  onEdit,
  onConfirm,
}: {
  view: ViewState;
  result: MfaRecoveryRequestResult | null;
  target: MfaRecoveryTarget | null;
  reasonCode: MfaRecoveryReasonCode | "";
  onReset: () => void;
  onEdit: () => void;
  onConfirm: () => void;
}): ReactNode | null {
  if (view === "success" && result) return <RecoverySuccessPanel result={result} onReset={onReset} />;
  if (view === "expired") return <RecoveryExpiredPanel onReset={onReset} />;
  if (view === "review" && target && reasonCode) {
    return (
      <>
        <RecoveryNotice />
        <RecoveryReviewPanel
          target={target}
          reasonCode={reasonCode}
          onEdit={onEdit}
          onConfirm={onConfirm}
        />
      </>
    );
  }
  return null;
}

interface RecoveryFormProps {
  accessToken: string;
  currentMemberId: string | null;
  initialTarget?: { member_id: string };
  expireSession: () => void;
}

function RecoveryRequestForm({ accessToken, currentMemberId, initialTarget, expireSession }: RecoveryFormProps) {
  const [target, setTarget] = useState<MfaRecoveryTarget | null>(null);
  const [reasonCode, setReasonCode] = useState<MfaRecoveryReasonCode | "">("");
  const [reasonDetail, setReasonDetail] = useState("");
  const [confirmedInPerson, setConfirmedInPerson] = useState(false);
  const [view, setView] = useState<ViewState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MfaRecoveryRequestResult | null>(null);

  function selectTarget(nextTarget: MfaRecoveryTarget | null) {
    setTarget(nextTarget);
    setError(null);
    setView("idle");
  }

  function resetForm() {
    setTarget(null);
    setReasonCode("");
    setReasonDetail("");
    setConfirmedInPerson(false);
    setError(null);
    setResult(null);
    setView("idle");
  }

  function reviewSubmission(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const formState = { target, currentMemberId, reasonCode, reasonDetail, confirmedInPerson };
    const validationError = validateForm(formState);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!target?.member_id || !reasonCode) return;

    setView("review");
  }

  async function confirmSubmission(requestTarget: MfaRecoveryTarget, requestReasonCode: MfaRecoveryReasonCode) {
    setError(null);
    setView("submitting");
    try {
      const response = await requestMfaRecovery(accessToken, {
        member_id: requestTarget.member_id,
        reason_code: requestReasonCode,
        reason_detail: reasonDetail.trim() || null,
      });
      setResult(response);
      setView("success");
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) {
        expireSession();
        return;
      }
      setError(messageForError(cause));
      setView(viewForError(cause));
    }
  }

  const terminalState = renderRecoveryTerminalState({
    view,
    result,
    target,
    reasonCode,
    onReset: resetForm,
    onEdit: () => setView("idle"),
    onConfirm: () => void confirmSubmission(target!, reasonCode as MfaRecoveryReasonCode),
  });
  if (terminalState) return terminalState;

  return (
    <>
      <RecoveryNotice />
      {error && <StatusBanner tone="error">{error}</StatusBanner>}
      {view === "conflict" && <Button type="button" variant="secondary" onClick={() => { setView("idle"); setError(null); }}>Revisar solicitação</Button>}
      <form onSubmit={reviewSubmission} noValidate>
        <AccessTargetPicker<MfaRecoveryTarget>
          accessToken={accessToken}
          label="Associado titular"
          memberOnly
          maxQueryLength={RECOVERY_TARGET_QUERY_MAX_LENGTH}
          initialTarget={initialTarget}
          searchTargets={searchRecoveryTargets}
          readTarget={readRecoveryTarget}
          onTargetChange={selectTarget}
        />
        <div className="field">
          <label className="field__label" htmlFor="mfa-recovery-reason">Motivo da recuperação</label>
          <select
            id="mfa-recovery-reason"
            className="field__input"
            value={reasonCode}
            onChange={(event) => setReasonCode(event.target.value as MfaRecoveryReasonCode | "")}
            required
          >
            <option value="">Selecione um motivo</option>
            {REASONS.map((reason) => <option key={reason.value} value={reason.value}>{reason.label}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="mfa-recovery-detail">Complemento do motivo <span className="field__optional">(opcional)</span></label>
          <textarea
            id="mfa-recovery-detail"
            className="field__input mfa-recovery-request__textarea"
            value={reasonDetail}
            maxLength={280}
            aria-describedby="mfa-recovery-detail-hint"
            onChange={(event) => setReasonDetail(event.target.value)}
          />
          <span className="field__hint" id="mfa-recovery-detail-hint">Até 280 caracteres, sem dados sensíveis ou quebras de linha.</span>
        </div>
        <label className="mfa-recovery-request__confirmation">
          <input type="checkbox" checked={confirmedInPerson} onChange={(event) => setConfirmedInPerson(event.target.checked)} />
          <span>Confirmo que o titular está presente na instituição e solicitou esta recuperação.</span>
        </label>
        <div className="form-actions mfa-recovery-request__actions">
          <Link className="btn btn--secondary" to="/dashboard">Cancelar</Link>
          <Button type="submit" loading={view === "submitting"}>Revisar solicitação</Button>
        </div>
      </form>
      {target?.member_id && reasonCode && <p className="mfa-recovery-request__review" aria-live="polite">Motivo selecionado: {reasonLabel(reasonCode)}.</p>}
    </>
  );
}

export function MfaRecoveryRequestPage() {
  const { state, expireSession } = useAuth();
  const [params] = useSearchParams();
  if (state.status !== "authenticated") return null;

  const currentMemberId = state.sessionContext?.member_id ?? state.memberId;
  const initialTarget = initialMemberTarget(params.get("member_id")?.trim() ?? null, currentMemberId);
  return (
    <main className="operational-page mfa-recovery-request-page">
      <Link className="page-back" to="/dashboard">Voltar para início</Link>
      <section className="form-card mfa-recovery-request-card" aria-labelledby="mfa-recovery-request-title">
        <p className="members-page__eyebrow">Secretaria e DAF</p>
        <h1 id="mfa-recovery-request-title">Solicitar recuperação de autenticador</h1>
        <p className="form-card__intro">Registre uma solicitação para um associado que está presencialmente na instituição e não consegue usar o autenticador. O sistema define o fluxo adequado e emite um protocolo rastreável.</p>
        <RecoveryRequestForm accessToken={state.accessToken} currentMemberId={currentMemberId} initialTarget={initialTarget} expireSession={expireSession} />
      </section>
    </main>
  );
}
